/**
 * PATH: packages/email-hub/src/feature/components/charts/chartSpec.ts
 * FILE_ID: EMAILHUB-CHARTS-SPEC-001
 * PURPOSE: The constrained chart/flowchart SPEC — types, strict validators
 *          and grid→spec extraction. Whether a spec comes from the manual
 *          builder, the spreadsheet's data, or an AI prompt (MR4), the SAME
 *          validated shape feeds the SAME library renderer.
 * WHY: FEAT-2026-07-03-COMPOSE-CHARTS — "generate it in a fashion where we
 *      can use AI to prompt … from pre-existing libraries to avoid
 *      hallucinations and bad design". AI never emits pixels or JSX — only
 *      this spec; anything outside the whitelist is rejected or coerced, so
 *      rendering is always recharts / React-Flow drawing validated data.
 * FLOW:
 *   unknown JSON ─▶ parseChartSpec / parseFlowSpec ─▶ spec | {error}
 *   A1 sheet + computed values ─▶ sheetToChartData ─▶ ChartData rows
 * RELATED:
 *   - chartRender.tsx: recharts/React-Flow renderers consuming these specs
 *   - ComposeChartModal.tsx: the builder UI
 *   - lib/aiChartSpec.ts (MR4): prompt → JSON → these validators
 */

export const CHART_KINDS = ['bar', 'line', 'area', 'pie'] as const
export type ChartKind = (typeof CHART_KINDS)[number]

/** Series palette — index-stable so AI output needs no colour knowledge. */
export const CHART_COLORS = [
  '#2563EB', '#16A34A', '#EA580C', '#9333EA', '#DC2626', '#0D9488',
  '#CA8A04', '#DB2777',
] as const

export interface ChartSeries {
  /** Key into each data row AND the legend label. */
  name: string
}

export interface ChartSpec {
  kind: ChartKind
  title: string
  /** Key of the category/x value in each data row. Always 'label'. */
  series: ChartSeries[]
  /** Rows: { label: string, [seriesName]: number } */
  data: Array<Record<string, string | number>>
}

export interface FlowNode {
  id: string
  label: string
}

export interface FlowEdge {
  from: string
  to: string
  label?: string
}

export interface FlowSpec {
  title: string
  nodes: FlowNode[]
  edges: FlowEdge[]
}

const MAX_SERIES = 8
const MAX_ROWS = 60
const MAX_NODES = 40
const MAX_EDGES = 80
const MAX_LABEL = 80

function cleanLabel(v: unknown, max = MAX_LABEL): string {
  return typeof v === 'string' ? v.trim().slice(0, max) : ''
}

function toNumber(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v
  if (typeof v === 'string') {
    const n = Number(v.replace(/[$,%\s]/g, ''))
    if (v.trim() !== '' && Number.isFinite(n)) return n
  }
  return null
}

export type ParseResult<T> = { ok: true; spec: T } | { ok: false; error: string }

/**
 * Strict whitelist validator: unknown JSON → ChartSpec.
 * Coerces numerics, drops unknown keys, bounds every collection; a spec
 * that ends up with no series or no usable rows is an error, never a
 * best-effort empty chart.
 */
export function parseChartSpec(raw: unknown): ParseResult<ChartSpec> {
  if (!raw || typeof raw !== 'object') return { ok: false, error: 'Spec must be a JSON object' }
  const obj = raw as Record<string, unknown>
  const kind = typeof obj.kind === 'string' ? obj.kind.toLowerCase() : ''
  if (!(CHART_KINDS as readonly string[]).includes(kind)) {
    return { ok: false, error: `kind must be one of ${CHART_KINDS.join(', ')}` }
  }
  const rawSeries = Array.isArray(obj.series) ? obj.series : []
  const series: ChartSeries[] = []
  const seen = new Set<string>()
  for (const s of rawSeries.slice(0, MAX_SERIES)) {
    const name = cleanLabel(
      typeof s === 'string' ? s : (s as Record<string, unknown>)?.name,
      40,
    )
    if (name === '' || name.toLowerCase() === 'label' || seen.has(name)) continue
    seen.add(name)
    series.push({ name })
  }
  if (series.length === 0) return { ok: false, error: 'At least one named series is required' }
  if (kind === 'pie' && series.length > 1) series.length = 1 // pies are single-series

  const rawData = Array.isArray(obj.data) ? obj.data : []
  const data: ChartSpec['data'] = []
  for (const entry of rawData.slice(0, MAX_ROWS)) {
    if (!entry || typeof entry !== 'object') continue
    const row = entry as Record<string, unknown>
    const label = cleanLabel(row.label ?? row.name ?? row.x, 60)
    if (label === '') continue
    const out: Record<string, string | number> = { label }
    let hasValue = false
    for (const s of series) {
      const n = toNumber(row[s.name])
      if (n !== null) { out[s.name] = n; hasValue = true }
    }
    if (hasValue) data.push(out)
  }
  if (data.length === 0) return { ok: false, error: 'No usable data rows (each row needs a label and at least one numeric series value)' }

  return {
    ok: true,
    spec: { kind: kind as ChartKind, title: cleanLabel(obj.title, 100), series, data },
  }
}

/** Strict whitelist validator: unknown JSON → FlowSpec. Edges referencing
 *  unknown nodes are dropped; a flow needs >= 2 nodes and >= 1 edge. */
export function parseFlowSpec(raw: unknown): ParseResult<FlowSpec> {
  if (!raw || typeof raw !== 'object') return { ok: false, error: 'Spec must be a JSON object' }
  const obj = raw as Record<string, unknown>
  const rawNodes = Array.isArray(obj.nodes) ? obj.nodes : []
  const nodes: FlowNode[] = []
  const ids = new Set<string>()
  for (const entry of rawNodes.slice(0, MAX_NODES)) {
    if (!entry || typeof entry !== 'object') continue
    const n = entry as Record<string, unknown>
    const id = cleanLabel(n.id, 40) || cleanLabel(n.label, 40)
    const label = cleanLabel(n.label, MAX_LABEL) || id
    if (id === '' || ids.has(id)) continue
    ids.add(id)
    nodes.push({ id, label })
  }
  if (nodes.length < 2) return { ok: false, error: 'A flowchart needs at least two nodes' }

  const rawEdges = Array.isArray(obj.edges) ? obj.edges : []
  const edges: FlowEdge[] = []
  for (const entry of rawEdges.slice(0, MAX_EDGES)) {
    if (!entry || typeof entry !== 'object') continue
    const e = entry as Record<string, unknown>
    const from = cleanLabel(e.from ?? e.source, 40)
    const to = cleanLabel(e.to ?? e.target, 40)
    if (!ids.has(from) || !ids.has(to) || from === to) continue
    const label = cleanLabel(e.label, 40)
    edges.push(label ? { from, to, label } : { from, to })
  }
  if (edges.length === 0) return { ok: false, error: 'A flowchart needs at least one edge between known nodes' }

  return { ok: true, spec: { title: cleanLabel(obj.title, 100), nodes, edges } }
}

/**
 * Spreadsheet → chart data ("graph creation in the excel-like builder").
 * Convention: row 1 = headers (A1 ignored, B1.. = series names), column A
 * from row 2 = labels, B2.. = numeric values (computed formula results
 * included via the values map). Returns null when the sheet doesn't fit
 * that shape.
 */
export function sheetToChartData(args: {
  /** raw cell text keyed by A1 ref */
  cells: Record<string, string>
  /** computed display values keyed by A1 ref (formula results) */
  values: Record<string, string>
  rows: number
  cols: number
}): { series: ChartSeries[]; data: ChartSpec['data'] } | null {
  const { cells, values, rows, cols } = args
  const val = (ref: string): string => {
    const raw = (cells[ref] ?? '').trim()
    return raw.startsWith('=') ? (values[ref] ?? '') : raw
  }
  const colLetter = (c: number): string => {
    let n = c + 1
    let out = ''
    while (n > 0) { out = String.fromCharCode(65 + ((n - 1) % 26)) + out; n = Math.floor((n - 1) / 26) }
    return out
  }
  const series: ChartSeries[] = []
  for (let c = 1; c < Math.min(cols, MAX_SERIES + 1); c++) {
    const name = cleanLabel(val(`${colLetter(c)}1`), 40)
    if (name !== '') series.push({ name })
  }
  if (series.length === 0) return null
  const data: ChartSpec['data'] = []
  for (let r = 2; r <= Math.min(rows, MAX_ROWS + 1); r++) {
    const label = cleanLabel(val(`A${r}`), 60)
    if (label === '') continue
    const row: Record<string, string | number> = { label }
    let hasValue = false
    series.forEach((s, idx) => {
      const n = toNumber(val(`${colLetter(idx + 1)}${r}`))
      if (n !== null) { row[s.name] = n; hasValue = true }
    })
    if (hasValue) data.push(row)
  }
  return data.length > 0 ? { series, data } : null
}
